import React, { useState } from "react";
import { Trash2, Loader2, AlertTriangle } from "lucide-react";
import api from "../../api";
import PasswordField from "../PasswordField";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";

// Leaving has to be as easy as joining. The only gate is the one that protects shoppers:
// a shop can't vanish while its pins are still live on the map.
export const DeleteAccountCard = () => {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const isBusiness = user?.role === "business";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/my/account/delete", { password, confirm });
      toast({ title: t("del.done"), description: t("del.doneNote") });
      logout();
      window.location.href = "/";
    } catch (err) {
      const d = err.response?.data?.detail;
      toast({ title: typeof d === "string" ? d : t("acc.pleaseTryAgain") });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-rose-500/40 rounded-2xl p-5" data-testid="delete-account-card">
      <h3 className="font-bold text-foreground flex items-center gap-2">
        <Trash2 size={18} className="text-rose-500" /> {t("del.title")}
      </h3>
      <p className="text-xs text-muted-foreground mt-1">{isBusiness ? t("del.subBusiness") : t("del.subShopper")}</p>

      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="delete-account-open-btn"
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-rose-600 border border-rose-500/50 hover:bg-rose-500/10 px-4 py-2.5 rounded-xl transition-colors">
          <Trash2 size={15} /> {t("del.cta")}
        </button>
      ) : (
        <form onSubmit={submit} className="mt-3 space-y-3">
          <p className="text-xs font-semibold text-rose-600 flex items-start gap-1.5" data-testid="delete-account-warning">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {t("del.warning")}
          </p>
          <PasswordField value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder={t("del.passwordPlaceholder")} testid="delete-account-password" />
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="delete-account-confirm"
            placeholder={t("del.typeDelete")}
            className="w-full bg-secondary rounded-xl px-3 py-2.5 text-sm font-semibold text-foreground outline-none focus:ring-2 ring-rose-500/40" />
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={busy || !password || confirm.trim().toUpperCase() !== "DELETE"}
              data-testid="delete-account-submit"
              className="inline-flex items-center gap-1.5 text-sm font-bold bg-rose-500 hover:bg-rose-600 disabled:opacity-60 text-white px-4 py-2.5 rounded-xl transition-colors">
              {busy ? <Loader2 className="animate-spin" size={15} /> : <Trash2 size={15} />} {t("del.confirmCta")}
            </button>
            <button type="button" onClick={() => { setOpen(false); setPassword(""); setConfirm(""); }}
              data-testid="delete-account-cancel"
              className="text-sm font-semibold text-muted-foreground hover:text-foreground px-4 py-2.5 rounded-xl">
              {t("del.keepAccount")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default DeleteAccountCard;
